import React, { JSX } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { RootState } from "../../store";
import { Employee } from "../../interfaces/User";
import { resourceCentreEmployeeActions } from "../../actions";

export enum IdSelector {
  EMPLOYEE = "employee",
  USER = "user"
}

export default function EmployeeMultiSelect({
  onChange,
  idSelector = IdSelector.USER,
  showNonUserEmployee = false,
  employeeIds = []
}: {
  onChange: (ids: number[]) => void;
  idSelector?: IdSelector;
  showNonUserEmployee?: boolean;
  employeeIds?: number[];
}): JSX.Element {
  const dispatch = useDispatch();

  const [value, setValue] = React.useState<Array<Employee>>([]);

  const rcId = useSelector((state: RootState) => state.userContext.resourceCentreId);
  const rcEmployees: Array<Employee> = useSelector(
    (state: RootState) => state.resources.resourceCentreEmployees
  );

  React.useEffect(() => {
    if (!rcEmployees?.length) {
      dispatch(
        resourceCentreEmployeeActions.getResourceCentreEmployees({ resourceCentreId: rcId })
      );
    }
  }, [rcId, dispatch, rcEmployees?.length]);

  React.useEffect(() => {
    if (rcEmployees?.length && employeeIds?.length) {
      const selectedEmployees = rcEmployees.filter((emp) => {
        if (idSelector === IdSelector.EMPLOYEE) {
          return employeeIds.includes(emp.id);
        }
        return emp.userAuthenticable?.some((ua) => employeeIds.includes(ua.id));
      });
      setValue(selectedEmployees);
    } else if (employeeIds?.length === 0) {
      setValue([]);
    }
  }, [rcEmployees, idSelector]);

  return (
    <Autocomplete
      multiple
      value={value}
      onChange={(_, newValue) => {
        setValue(newValue);
        if (idSelector === IdSelector.EMPLOYEE) {
          onChange(newValue.map(({ id }) => id));
        } else {
          onChange(newValue.map(({ userAuthenticable }) => userAuthenticable[0].id));
        }
      }}
      options={(rcEmployees || []).filter((item) => {
        if (showNonUserEmployee) return true;
        return !item.nonUserEmployee;
      })}
      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={option.id}
            size="small"
            label={`${option.firstName} ${option.lastName}`}
            //   eslint-disable-next-line react/jsx-props-no-spreading
            {...getTagProps({ index })}
          />
        ))
      }
      style={{ marginTop: "8px" }}
      renderInput={(params) => (
        //   eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} fullWidth label="Select Employees" />
      )}
    />
  );
}
